
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '@/src/contexts/AuthContext';
import { db, collection, getDocs, addDoc, doc, updateDoc, OperationType, handleFirestoreError } from '../firebase';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialVentureId?: string;
}

const OptimizeUpgradeFlow: React.FC<Props> = ({ isOpen, onClose, initialVentureId }) => {
  const { user, userProfile, activateOptimize } = useAuth();
  const [step, setStep] = useState(1);
  const [savedVentures, setSavedVentures] = useState<any[]>([]);
  const [selectedVenture, setSelectedVenture] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState({
    startDate: new Date().toISOString().split('T')[0],
    status: 'launching',
    connectionType: 'manual'
  });

  useEffect(() => {
    if (isOpen && user) {
      const fetchVentures = async () => {
        try {
          const snapshot = await getDocs(collection(db, 'users', user.uid, 'savedIdeas'));
          const ventures = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setSavedVentures(ventures);
          
          if (initialVentureId) {
            const initial = ventures.find(v => v.id === initialVentureId);
            if (initial) setSelectedVenture(initial);
          }
        } catch (e) {
          console.error('Error fetching ventures:', e);
        }
      };
      fetchVentures();
    }
  }, [isOpen, user, initialVentureId]);

  if (!isOpen) return null;

  const handleConfirmUpgrade = async () => {
    setLoading(true);
    try {
      if (!user) return;

      // 1. Create the Optimize Business document
      const businessData = {
        title: selectedVenture?.title || 'New Venture',
        category: selectedVenture?.category || 'General',
        location: selectedVenture?.location || 'Global',
        sourceVentureId: selectedVenture?.id || null,
        connectionStatus: 'pending',
        performanceData: JSON.stringify({}),
        liveSwot: JSON.stringify({}),
        onboarding: onboardingData,
        timestamp: Date.now()
      };

      await addDoc(collection(db, 'users', user.uid, 'optimize', 'businesses'), businessData);

      // 2. Activate Optimize flag in user profile
      await activateOptimize();

      // 3. Move to final step
      setStep(5);
    } catch (e) {
      handleFirestoreError(e, OperationType.WRITE, `users/${user?.uid}/optimize/businesses`);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <motion.div 
            key="optimize-step-1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-2">
              <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.25em] font-heading">Step 01 // Preservation</span>
              <h2 className="text-2xl font-bold tracking-tight font-heading text-white leading-tight">Preserve Confirmation</h2>
              <p className="text-xs font-medium text-white/60 leading-relaxed">
                Your Launchpad and Discovery access continues exactly as before. 
                You're adding new capabilities to scale what you've already validated.
              </p>
            </div>
            <button 
              onClick={() => setStep(2)}
              className="w-full py-3.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-xs tracking-widest rounded-lg transition-all font-heading cursor-pointer"
            >
              Confirm and Continue
            </button>
          </motion.div>
        );
      case 2:
        return (
          <motion.div 
            key="optimize-step-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-2">
              <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.25em] font-heading">Step 02 // Selection</span>
              <h2 className="text-2xl font-bold tracking-tight font-heading text-white leading-tight">Venture Selection</h2>
              <p className="text-xs font-medium text-white/60 leading-relaxed">
                Which validated venture are you actively building — or closest to launching?
              </p>
            </div>
            
            <div className="grid grid-cols-1 gap-3 max-h-[280px] overflow-y-auto pr-1 custom-scrollbar">
              {savedVentures.map((venture, idx) => (
                <button 
                  key={`${venture.id}-${idx}`}
                  onClick={() => setSelectedVenture(venture)}
                  className={`p-4 border rounded-lg text-left transition-all flex items-center justify-between cursor-pointer ${
                    selectedVenture?.id === venture.id 
                      ? 'border-[#F26522] bg-[#F26522]/10 text-white' 
                      : 'border-white/[0.06] hover:border-white/20 bg-[#322D29] text-white/90'
                  }`}
                >
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-tight text-white">{venture.title}</h4>
                    <p className="text-[10px] font-medium text-white/50 mt-0.5">{venture.location} • Score: {venture.score}</p>
                  </div>
                  <div className={`w-6 h-6 rounded flex items-center justify-center border transition-colors ${
                    selectedVenture?.id === venture.id ? 'bg-[#F26522] border-[#F26522] text-white' : 'border-white/10 text-transparent'
                  }`}>
                    <i className="fas fa-check text-[9px]"></i>
                  </div>
                </button>
              ))}
              <button 
                onClick={() => setSelectedVenture({ id: 'new', title: 'New Venture' })}
                className={`p-4 border rounded-lg text-left transition-all flex items-center justify-between italic cursor-pointer ${
                  selectedVenture?.id === 'new' 
                    ? 'border-[#F26522] bg-[#F26522]/10 text-white' 
                    : 'border-white/[0.08] border-dashed hover:border-white/20 bg-[#322D29] text-white/70'
                }`}
              >
                <span className="text-xs font-medium text-white/70">Building something else — manual entry</span>
                <i className="fas fa-plus text-white/40 text-[10px]"></i>
              </button>
            </div>

            <div className="flex gap-3 pt-2">
              <button 
                onClick={() => setStep(1)}
                className="flex-1 py-3.5 border border-white/10 text-white/70 hover:text-white hover:bg-white/5 font-bold uppercase text-[10px] tracking-wider rounded-lg transition-colors cursor-pointer"
              >
                Back
              </button>
              <button 
                disabled={!selectedVenture}
                onClick={() => setStep(3)}
                className="flex-[2] py-3.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-xs tracking-widest rounded-lg transition-all disabled:opacity-50 cursor-pointer"
              >
                This One
              </button>
            </div>
          </motion.div>
        );
      case 3:
        return (
          <motion.div 
            key="optimize-step-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-2">
              <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.25em] font-heading">Step 03 // Onboarding</span>
              <h2 className="text-2xl font-bold tracking-tight font-heading text-white leading-tight">Optimize Onboarding</h2>
              <p className="text-xs font-medium text-white/60 leading-relaxed">
                Connect your business data and confirm your current status to link your baseline.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Launch Date / Start Date</label>
                <input 
                  type="date" 
                  value={onboardingData.startDate}
                  onChange={(e) => setOnboardingData({...onboardingData, startDate: e.target.value})}
                  className="w-full bg-[#322D29] border border-white/[0.08] px-4 py-2.5 text-xs text-white rounded-lg focus:border-[#F26522] outline-none transition-colors"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Current Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {['launching', 'operating', 'scaling', 'pivoting'].map((status, idx) => (
                    <button 
                      key={`onboarding-status-${status}-${idx}`}
                      onClick={() => setOnboardingData({...onboardingData, status})}
                      className={`py-2.5 text-[9px] font-bold uppercase tracking-wider rounded-lg border transition-all cursor-pointer ${
                        onboardingData.status === status ? 'bg-[#F26522] text-white border-[#F26522]' : 'bg-[#322D29] text-white/60 border-white/[0.06] hover:border-white/20'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/60">Data Connection</label>
                <div className="p-3.5 bg-[#322D29] border border-white/[0.06] rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <i className="fas fa-plug text-[#F26522] text-xs"></i>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">Manual Data Entry (Default)</span>
                  </div>
                  <span className="text-[8px] font-bold bg-[#F26522] text-white px-2 py-0.5 uppercase tracking-wider rounded">Active</span>
                </div>
                <p className="text-[9px] font-medium text-white/40">POS & Accounting integrations available after setup.</p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button 
                onClick={() => setStep(2)}
                className="flex-1 py-3.5 border border-white/10 text-white/70 hover:text-white hover:bg-white/5 font-bold uppercase text-[10px] tracking-wider rounded-lg transition-colors cursor-pointer"
              >
                Back
              </button>
              <button 
                onClick={() => setStep(4)}
                className="flex-[2] py-3.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-xs tracking-widest rounded-lg transition-all cursor-pointer"
              >
                Next: Pricing
              </button>
            </div>
          </motion.div>
        );
      case 4:
        const currentPrice = userProfile?.subscriptionTier === 'premium' ? 19 : 0;
        const addOnPrice = 49;
        const total = currentPrice + addOnPrice;

        return (
          <motion.div 
            key="optimize-step-4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-2">
              <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.25em] font-heading">Step 04 // Billing</span>
              <h2 className="text-2xl font-bold tracking-tight font-heading text-white leading-tight">Pricing Integration</h2>
              <p className="text-xs font-medium text-white/60 leading-relaxed">
                Optimize added to your current plan. Your existing billing cycle remains unchanged.
              </p>
            </div>

            <div className="bg-[#322D29] border border-white/[0.06] rounded-xl p-6 space-y-4">
              <div className="flex justify-between items-center border-b border-white/[0.06] pb-3">
                <div className="space-y-0.5">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-white/50">Current Plan</span>
                  <p className="text-xs font-bold uppercase text-white">{userProfile?.subscriptionTier || 'Free'}</p>
                </div>
                <p className="text-sm font-bold text-white font-heading">${currentPrice}/mo</p>
              </div>
              
              <div className="flex justify-between items-center border-b border-white/[0.06] pb-3">
                <div className="space-y-0.5">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522]">Optimize Add-on</span>
                  <p className="text-xs font-bold uppercase text-white">Live Performance Scaling</p>
                </div>
                <p className="text-sm font-bold text-[#F26522] font-heading">+${addOnPrice}/mo</p>
              </div>

              <div className="flex justify-between items-center pt-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">New Monthly Total</span>
                <p className="text-2xl font-bold text-white tracking-tight font-heading">${total}/mo</p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button 
                onClick={() => setStep(3)}
                className="flex-1 py-3.5 border border-white/10 text-white/70 hover:text-white hover:bg-white/5 font-bold uppercase text-[10px] tracking-wider rounded-lg transition-colors cursor-pointer"
              >
                Back
              </button>
              <button 
                disabled={loading}
                onClick={handleConfirmUpgrade}
                className="flex-[2] py-3.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-xs tracking-widest rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer shadow-none"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>Confirm Upgrade <i className="fas fa-arrow-right text-xs"></i></>
                )}
              </button>
            </div>
          </motion.div>
        );
      case 5:
        return (
          <motion.div 
            key="optimize-step-5"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center space-y-6 py-6"
          >
            <div className="w-14 h-14 bg-emerald-950/40 text-emerald-400 border border-emerald-500/40 rounded-xl flex items-center justify-center text-2xl mx-auto">
              <i className="fas fa-check"></i>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tight font-heading text-white leading-tight">Optimize Activated</h2>
              <p className="text-xs font-medium text-white/60 leading-relaxed max-w-sm mx-auto">
                Your venture is now linked to the Optimize dashboard. Start tracking actual performance against your feasibility baseline.
              </p>
            </div>
            <button 
              onClick={() => {
                onClose();
                window.location.href = '/?tab=optimize';
              }}
              className="w-full py-3.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-xs tracking-widest rounded-lg transition-all font-heading cursor-pointer"
            >
              Launch Optimize Dashboard
            </button>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/75 animate-in fade-in duration-200">
      <div className="w-full max-w-xl bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-2xl relative overflow-hidden text-white">
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-white/[0.06]">
          <motion.div 
            className="h-full bg-[#F26522]"
            initial={{ width: '0%' }}
            animate={{ width: `${(step / 5) * 100}%` }}
          />
        </div>

        <button 
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition-colors absolute top-5 right-5 z-10 cursor-pointer"
        >
          <i className="fas fa-times text-base"></i>
        </button>

        <div className="p-8 sm:p-10">
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default OptimizeUpgradeFlow;
